import { formatCurrency } from '@/utils/formatCurrency';

export type PerformanceMetricId = 'sales' | 'orders' | 'spend';

export type PerformanceGranularity = 'daily' | 'weekly' | 'monthly';

export type PerformanceViewMode = 'graph' | 'table';

export type PerformanceDataPoint = {
  date: string;
  sales: number;
  orders: number;
  spend: number;
};

export type PerformanceMetricDef = {
  id: PerformanceMetricId;
  label: string;
  color: string;
  fillColor: string;
  axis: 'left-inner' | 'left-outer' | 'right';
  format: 'currency' | 'count';
};

/** Workspace-aligned metric colors: Sales violet, Orders teal, Spend amber. */
export const PERFORMANCE_METRIC_DEFS: PerformanceMetricDef[] = [
  {
    id: 'sales',
    label: 'Sales',
    color: 'rgb(124 58 237)',
    fillColor: 'rgba(124, 58, 237, 0.12)',
    axis: 'left-inner',
    format: 'currency',
  },
  {
    id: 'orders',
    label: 'Orders',
    color: 'rgb(13 148 136)',
    fillColor: 'rgba(13, 148, 136, 0.12)',
    axis: 'right',
    format: 'count',
  },
  {
    id: 'spend',
    label: 'Spend',
    color: 'rgb(245 158 11)',
    fillColor: 'rgba(245, 158, 11, 0.12)',
    axis: 'left-outer',
    format: 'currency',
  },
];

export const DEFAULT_PERFORMANCE_METRIC_IDS: PerformanceMetricId[] = ['sales', 'orders', 'spend'];

export function performanceMetricDef(id: PerformanceMetricId): PerformanceMetricDef {
  const def = PERFORMANCE_METRIC_DEFS.find((entry) => entry.id === id);
  if (!def) throw new Error(`Unknown performance metric: ${id}`);
  return def;
}

export function formatPerformanceAxisValue(value: number, format: PerformanceMetricDef['format']): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'count') {
    return new Intl.NumberFormat('en-IN', {
      notation: value >= 10_000 ? 'compact' : 'standard',
      compactDisplay: 'short',
      maximumFractionDigits: value >= 10_000 ? 1 : 0,
    }).format(value);
  }
  return formatCurrency(value, undefined, { notation: 'compact', maximumFractionDigits: 1, empty: '—' });
}

export function formatPerformanceTooltipValue(value: number, format: PerformanceMetricDef['format']): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'count') {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
    }).format(value);
  }
  return formatCurrency(value, undefined, { notation: 'compact', maximumFractionDigits: 1, empty: '—' });
}

export function formatPerformanceTableValue(value: number, format: PerformanceMetricDef['format']): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'count') {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
  }
  return formatCurrency(value, undefined, {
    empty: '—',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
